import { withStyles } from '@material-ui/core/styles';
import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';
import React, { useCallback, useContext } from 'react';
import { OrderReviewContext } from '../../../../QaOrderReview/OrderReviewContext';
import ReactSelectWrapper from '../../../ReactSelectWrapper';
import {
  createSelectOptions,
  getOtherAnswer,
  multiSelectIsChecked,
  removeChildAnswers,
} from '../../FormDefinitionInput.utils';
import OtherTextField from '../OtherTextField';
import styles from './MultiSelect.styles';

const propTypes = {
  classes: PropTypes.object.isRequired,
  checkIfAnswerTriggersNewFormHeader: PropTypes.func.isRequired,
  formDefinition: PropTypes.object.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  imageToEdit: PropTypes.object,
  removeAnswers: PropTypes.func.isRequired,
  saveAnswers: PropTypes.func.isRequired,
  updateFormHeaders: PropTypes.func.isRequired,
};

const MultiSelect = ({
  classes,
  checkIfAnswerTriggersNewFormHeader,
  formDefinition,
  isReadOnly,
  imageToEdit,
  removeAnswers,
  saveAnswers,
  updateFormHeaders,
}) => {
  const { dispatch: orderReviewDispatch } = useContext(OrderReviewContext);
  const saveAnswersDebounceHandler = useCallback(
    debounce(newAnswers => {
      saveAnswers(newAnswers);
    }, 750),
    [],
  );

  const isAnswerAnIssue = answerValue => {
    if (answerValue) {
      return !(
        (answerValue.toLowerCase().includes('no') && answerValue.toLowerCase().includes('issues')) ||
        answerValue.toLowerCase().includes('none')
      );
    } else {
      return false;
    }
  };

  const handleOnChange = async event => {
    const eventAnswerTrigger = formDefinition.answerTriggers.find(
      at => at.answerValueId.toString() === event.target?.value.toString(),
    );
    const eventAnswerValue = eventAnswerTrigger ? eventAnswerTrigger.answerValue : null;
    const multiSelectAnswerValueId = parseInt(event.target.value);

    // when a question gets added and there are answers but not tagged to the photo
    // add the photo to the answers
    // 1. check if there is a photo to be edited
    // 2. check if other answers have an empty images array
    // 3. add the imageToEdit to each image array

    let newAnswers = formDefinition.answers.map(answer => ({
      orderFormHeaderId: formDefinition.orderFormHeaderId,
      formDefinitionId: formDefinition.formDefinitionId,
      questionId: formDefinition.questionId,
      formQaAnswerId: answer.formQaAnswerId,
      type: formDefinition.answerType,
      valueId: answer.valueId,
      value: answer.value,
      text: null,
      modifiedOn: new Date(),
      images: answer.images,
      default: answer.default,
    }));
    const existingAnswer = newAnswers.find(answer => answer.valueId === multiSelectAnswerValueId);
    const isSelectedAnswerAnIssue = isAnswerAnIssue(eventAnswerValue);

    if (isSelectedAnswerAnIssue) {
      newAnswers = formDefinition.answers.filter(answer => isAnswerAnIssue(answer.value));
    } else if (!existingAnswer) {
      newAnswers = [];
    }

    // if the answer selected already exists
    // for select boxes this should always be false
    // unless clicking on x when added to the form and not attatched to photo
    if (existingAnswer) {
      let answerExistsElsewhere = false;
      if (!imageToEdit) {
        // if not removing from a single image, then clear the entire answer (and all images with it)
        // the answer may exist somewhere else, but keeping this flag as false will clear the entire answer from the formDefinition
        answerExistsElsewhere = false;
      } else if (existingAnswer.images.length === 1) {
        if (existingAnswer.images[0].formQaImageId !== imageToEdit.formQaImageId) {
          answerExistsElsewhere = true;
        }
      } else if (existingAnswer.images.length > 0) {
        answerExistsElsewhere = true;
      } else if (!existingAnswer.images?.length && imageToEdit) {
        // the answer is not currently attached to any images and needs to be added
        answerExistsElsewhere = true;

        // if we answer questions from the form and then try and remove them
        // in Edit Photo, you will have to remove them twice
        // first time adds the answer, second time removes the answer
        if (imageToEdit && existingAnswer.images.length < 1) {
          answerExistsElsewhere = false;
        }
      }

      // Does answer exist on another image?
      // Example: Peeling paint is answered as a liability issue on both front of
      // house picture and back of house picture
      if (answerExistsElsewhere) {
        newAnswers = newAnswers.map(answer => {
          if (answer.valueId === multiSelectAnswerValueId) {
            // Is it currently checked?
            let isChecked = answer.images.some(i => imageToEdit.formQaImageId === i.formQaImageId);
            if (isChecked) {
              // If only one image on answer, remove answer
              if (answer.images.length > 1) {
                // If it is, remove image
                answer.images = answer.images.filter(i => i.formQaImageId !== imageToEdit.formQaImageId);
                return answer;
              }
            } else {
              // If it isn't, add image
              answer.images = answer.images.concat(imageToEdit);
              return answer;
            }
          } else {
            return answer;
          }
        });
      } else {
        newAnswers = newAnswers.filter(answer => answer.valueId !== multiSelectAnswerValueId);
        removeChildAnswers(formDefinition, multiSelectAnswerValueId, true, orderReviewDispatch, removeAnswers);
      }
    } else {
      // for multi select boxes we should hit this always
      // because we should never select the same answer again
      // it will be gone from the select box
      newAnswers.push({
        orderFormHeaderId: formDefinition.orderFormHeaderId,
        formDefinitionId: formDefinition.formDefinitionId,
        questionId: formDefinition.questionId,
        formQaAnswerId: null,
        type: formDefinition.answerType,
        valueId: multiSelectAnswerValueId,
        value: eventAnswerValue,
        text: null,
        modifiedOn: new Date(),
        images: imageToEdit ? [imageToEdit] : [],
        default: formDefinition.answerTriggers.find(at => at.answerValueId === multiSelectAnswerValueId).default,
      });
      checkIfAnswerTriggersNewFormHeader(multiSelectAnswerValueId);

      // if we add an answer that has an image, we need to
      // give that image to any existing answers
      // adding a new answer in the select box should behave the same way
      // if we press the add button
      if (imageToEdit && newAnswers.some(na => na.images.length < 1)) {
        newAnswers = newAnswers.map(na => {
          return { ...na, images: [imageToEdit] };
        });
      }

      // the reverse of the above, when we answer a question that has answers
      // but we are not tagging it to the photo, we still need to add the imageToEdit
      const imageToTag = newAnswers.filter(na => na.images.length === 1)[0]?.images;
      newAnswers = newAnswers?.map(wi => {
        if (wi.images.length < 1) {
          return { ...wi, images: imageToTag ? imageToTag : [] };
        } else {
          return { ...wi };
        }
      });
    }

    updateFormHeaders(newAnswers);
    if (newAnswers.length === 0) {
      removeAnswers(formDefinition.answers);
    } else {
      saveAnswersDebounceHandler(newAnswers);
    }
  };

  const otherAnswer = getOtherAnswer(formDefinition, imageToEdit);

  const multiSelectOptions = createSelectOptions(formDefinition);

  return (
    <React.Fragment>
      <ReactSelectWrapper
        isMulti
        isClearable={false}
        isDisabled={isReadOnly}
        options={multiSelectOptions}
        value={multiSelectOptions.filter(o =>
          multiSelectIsChecked(formDefinition, imageToEdit, o.value, formDefinition.formDefinitionId),
        )}
        onChange={handleOnChange}
      />
      {otherAnswer && (
        <OtherTextField
          formDefinition={formDefinition}
          isReadOnly={isReadOnly}
          imageToEdit={imageToEdit}
          otherAnswer={otherAnswer}
          removeAnswers={removeAnswers}
          saveAnswers={saveAnswers}
          updateFormHeaders={updateFormHeaders}
        />
      )}
    </React.Fragment>
  );
};

MultiSelect.propTypes = propTypes;
export default withStyles(styles)(MultiSelect);
